import { PhoneField } from "@equidefi/ui/forms";
import type { PhoneFieldProps } from "@equidefi/ui/forms/PhoneField";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";

export type SmartPhoneFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<PhoneFieldProps, "name" | "label" | "value" | "onChange"> & {
  name: TName;
  label?: string;
  helperText?: string;
  isRequired?: boolean;
};

function SmartPhoneField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, label, helperText, isRequired, ...phoneProps }: SmartPhoneFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    name,
    control,
  });

  return (
    <PhoneField
      name={field.name}
      ref={field.ref}
      value={field.value ?? ""}
      onChange={(value) => field.onChange(value ?? "")}
      label={label as string}
      isInvalid={!!error}
      isRequired={isRequired}
      helperText={helperText}
      error={error?.message}
      {...phoneProps}
    />
  );
}

export default SmartPhoneField;
